import React, { useEffect, useState } from 'react';
import supportService from '../services/supportService';
import './SupportSettings.css';

const SupportSettings = () => {
  const [loading, setLoading] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);
  const [savingFaq, setSavingFaq] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [settings, setSettings] = useState({
    support_email: '',
    support_phone: '',
    support_message: '',
  });

  const [faqs, setFaqs] = useState([]);
  const [newFaq, setNewFaq] = useState({
    question: '',
    answer: '',
  });

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError('');

        const [{ settings: s }, { faqs: f }] = await Promise.all([
          supportService.getSettings(),
          supportService.getFaqs(),
        ]);

        setSettings({
          support_email: s?.support_email || '',
          support_phone: s?.support_phone || '',
          support_message: s?.support_message || '',
        });
        setFaqs(Array.isArray(f) ? f : []);
      } catch (err) {
        console.error('Failed to load support settings', err);
        setError('Failed to load support settings. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  const handleSettingsChange = (e) => {
    const { name, value } = e.target;
    setSettings((prev) => ({ ...prev, [name]: value }));
  };

  const handleSaveSettings = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      setSavingSettings(true);
      await supportService.updateSettings(settings);
      setSuccess('Support settings updated successfully.');
    } catch (err) {
      console.error('Failed to save support settings', err);
      const msg =
        err?.response?.data?.message ||
        'Failed to save support settings. Please try again.';
      setError(msg);
    } finally {
      setSavingSettings(false);
    }
  };

  const handleFaqFieldChange = (id, field, value) => {
    setFaqs((prev) =>
      prev.map((faq) =>
        faq.id === id
          ? {
              ...faq,
              [field]: value,
            }
          : faq
      )
    );
  };

  const handleToggleFaqActive = async (faq) => {
    setError('');
    setSuccess('');
    setSavingFaq(true);
    try {
      await supportService.updateFaq(faq.id, {
        is_active: !faq.is_active,
      });
      setFaqs((prev) =>
        prev.map((item) =>
          item.id === faq.id ? { ...item, is_active: !faq.is_active } : item
        )
      );
    } catch (err) {
      console.error('Failed to update FAQ', err);
      const msg =
        err?.response?.data?.message ||
        'Failed to update FAQ. Please try again.';
      setError(msg);
    } finally {
      setSavingFaq(false);
    }
  };

  const handleSaveFaq = async (faq) => {
    setError('');
    setSuccess('');
    setSavingFaq(true);
    try {
      await supportService.updateFaq(faq.id, {
        question: faq.question,
        answer: faq.answer,
        display_order: faq.display_order,
        is_active: faq.is_active,
      });
      setSuccess('FAQ updated.');
    } catch (err) {
      console.error('Failed to update FAQ', err);
      const msg =
        err?.response?.data?.message ||
        'Failed to update FAQ. Please try again.';
      setError(msg);
    } finally {
      setSavingFaq(false);
    }
  };

  const handleDeleteFaq = async (faq) => {
    if (!window.confirm('Delete this FAQ?')) return;

    setError('');
    setSuccess('');
    setSavingFaq(true);
    try {
      await supportService.deleteFaq(faq.id);
      setFaqs((prev) => prev.filter((item) => item.id !== faq.id));
      setSuccess('FAQ deleted.');
    } catch (err) {
      console.error('Failed to delete FAQ', err);
      const msg =
        err?.response?.data?.message ||
        'Failed to delete FAQ. Please try again.';
      setError(msg);
    } finally {
      setSavingFaq(false);
    }
  };

  const handleNewFaqChange = (e) => {
    const { name, value } = e.target;
    setNewFaq((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCreateFaq = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!newFaq.question || !newFaq.answer) {
      setError('Question and answer are required for a new FAQ.');
      return;
    }

    try {
      setSavingFaq(true);
      const { faq } = await supportService.createFaq({
        question: newFaq.question,
        answer: newFaq.answer,
        is_active: true,
      });
      setFaqs((prev) => [...prev, faq]);
      setNewFaq({
        question: '',
        answer: '',
      });
      setSuccess('FAQ created.');
    } catch (err) {
      console.error('Failed to create FAQ', err);
      const msg =
        err?.response?.data?.message ||
        'Failed to create FAQ. Please try again.';
      setError(msg);
    } finally {
      setSavingFaq(false);
    }
  };

  if (loading) {
    return (
      <div className="support-settings-page">
        <h1 className="page-title">Support &amp; FAQ</h1>
        <p>Loading support settings...</p>
      </div>
    );
  }

  return (
    <div className="support-settings-page">
      <h1 className="page-title">Support &amp; FAQ</h1>
      <p className="page-subtitle">
        Configure the public support contact information and help center FAQs.
      </p>

      {error && <div className="admin-alert admin-alert-error">{error}</div>}
      {success && (
        <div className="admin-alert admin-alert-success">{success}</div>
      )}

      <div className="support-settings-grid">
        <section className="card">
          <h2>Support contact details</h2>
          <form onSubmit={handleSaveSettings} className="support-settings-form">
            <div className="form-group">
              <label htmlFor="support_email">Support email</label>
              <input
                id="support_email"
                name="support_email"
                type="email"
                value={settings.support_email}
                onChange={handleSettingsChange}
                placeholder="support@example.com"
              />
              <p className="hint">
                This email is shown on the storefront and used as the primary
                reply-to for support.
              </p>
            </div>
            <div className="form-group">
              <label htmlFor="support_phone">Support phone</label>
              <input
                id="support_phone"
                name="support_phone"
                type="tel"
                value={settings.support_phone}
                onChange={handleSettingsChange}
                placeholder="+977 ..."
              />
            </div>
            <div className="form-group">
              <label htmlFor="support_message">Support hero message</label>
              <textarea
                id="support_message"
                name="support_message"
                rows="3"
                value={settings.support_message}
                onChange={handleSettingsChange}
                placeholder="Short message shown on the Help & Support page."
              />
            </div>
            <button
              type="submit"
              className="btn-primary"
              disabled={savingSettings}
            >
              {savingSettings ? 'Saving...' : 'Save support settings'}
            </button>
          </form>
        </section>

        <section className="card">
          <h2>Frequently asked questions</h2>
          <p className="hint">
            Active FAQs are shown on the customer Help &amp; Support page in a
            collapsible list.
          </p>

          <div className="faq-list-admin">
            {faqs.map((faq) => (
              <div key={faq.id} className="faq-row">
                <div className="faq-row-header">
                  <input
                    type="text"
                    value={faq.question}
                    onChange={(e) =>
                      handleFaqFieldChange(faq.id, 'question', e.target.value)
                    }
                    placeholder="Question"
                  />
                </div>
                <div className="faq-row-body">
                  <textarea
                    rows="2"
                    value={faq.answer}
                    onChange={(e) =>
                      handleFaqFieldChange(faq.id, 'answer', e.target.value)
                    }
                    placeholder="Answer"
                  />
                  <div className="faq-row-meta">
                    <label className="checkbox">
                      <input
                        type="checkbox"
                        checked={!!faq.is_active}
                        onChange={() => handleToggleFaqActive(faq)}
                      />
                      Active
                    </label>
                    <div className="faq-row-actions">
                      <button
                        type="button"
                        className="btn-secondary"
                        onClick={() => handleSaveFaq(faq)}
                        disabled={savingFaq}
                      >
                        Save
                      </button>
                      <button
                        type="button"
                        className="btn-danger"
                        onClick={() => handleDeleteFaq(faq)}
                        disabled={savingFaq}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <form className="new-faq-form" onSubmit={handleCreateFaq}>
            <h3>Add new FAQ</h3>
            <div className="form-group">
              <label htmlFor="new-question">Question</label>
              <input
                id="new-question"
                name="question"
                type="text"
                value={newFaq.question}
                onChange={handleNewFaqChange}
                placeholder="e.g. How long does shipping take?"
              />
            </div>
            <div className="form-group">
              <label htmlFor="new-answer">Answer</label>
              <textarea
                id="new-answer"
                name="answer"
                rows="2"
                value={newFaq.answer}
                onChange={handleNewFaqChange}
              />
            </div>
            <div className="form-group">
            </div>
            <button
              type="submit"
              className="btn-primary"
              disabled={savingFaq}
            >
              {savingFaq ? 'Adding...' : 'Add FAQ'}
            </button>
          </form>
        </section>
      </div>
    </div>
  );
};

export default SupportSettings;

